import React, { Component, ErrorInfo } from 'react';

import * as Sentry from '@sentry/react';
import { Button } from '@sobrus-com/sobrus-design-system-v2';

import PageLoader from '../pageLoader/PageLoader';

interface ErrorBoundaryProps {
	children: React.ReactNode;
	resetKey?: string;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

const CHUNK_RELOAD_KEY = 'chunk_reload_attempt';

export const isChunkLoadError = (error?: Error | null): boolean => {
	if (!error) return false;
	const message = error.message || '';
	return (
		error.name === 'ChunkLoadError' ||
		message.includes('Failed to fetch dynamically imported module') ||
		message.includes('Importing a module script failed') ||
		message.includes('error loading dynamically imported module')
	);
};

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	private clearTimer?: ReturnType<typeof setTimeout>;

	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: undefined };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidMount() {
		// If the app successfully loaded and remained healthy, clear the reload guard after 10s
		if (typeof window !== 'undefined' && sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
			this.clearTimer = setTimeout(() => {
				sessionStorage.removeItem(CHUNK_RELOAD_KEY);
			}, 10000);
		}
	}

	componentWillUnmount() {
		if (this.clearTimer) {
			clearTimeout(this.clearTimer);
		}
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		const isChunk = isChunkLoadError(error);

		// Handle chunk load errors with a single-reload guard to prevent infinite loops
		if (isChunk) {
			const alreadyReloaded =
				typeof window !== 'undefined' && sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';

			if (!alreadyReloaded) {
				sessionStorage.setItem(CHUNK_RELOAD_KEY, 'true');
				window.location.reload();
				return;
			}
		}

		Sentry.captureException(error, {
			level: 'fatal',
			tags: {
				crash_type: 'handled',
				isBreaking: 'true',
				chunk_load_failed: isChunk ? 'true' : 'false',
			},
			contexts: {
				react: {
					componentStack: errorInfo.componentStack,
				},
			},
			extra: {
				errorMessage: error.message,
			},
		});
	}

	handleReload = () => {
		if (typeof window !== 'undefined') {
			sessionStorage.removeItem(CHUNK_RELOAD_KEY);
			window.location.reload();
		}
	};

	handleGoHome = () => {
		if (typeof window !== 'undefined') {
			sessionStorage.removeItem(CHUNK_RELOAD_KEY);
			window.location.href = '/';
		}
	};

	render() {
		const { hasError, error } = this.state;

		if (hasError) {
			const isChunk = isChunkLoadError(error);
			const alreadyReloaded =
				typeof window !== 'undefined' && sessionStorage.getItem(CHUNK_RELOAD_KEY) === 'true';

			// While the initial automatic reload executes, display the loader
			if (isChunk && !alreadyReloaded) {
				return <PageLoader />;
			}

			return (
				<div
					className="sns"
					style={{
						minHeight: '60vh',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '2rem',
					}}
				>
					<div
						style={{
							maxWidth: 520,
							width: '100%',
							textAlign: 'center',
							background: '#ffffff',
							borderRadius: 12,
							padding: '2.5rem 2rem',
							boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
							border: '1px solid #eef0f4',
						}}
					>
						<h2
							style={{
								fontSize: '1.4rem',
								fontWeight: 600,
								color: '#1a1f36',
								marginBottom: '0.75rem',
							}}
						>
							{isChunk ? 'Mise à jour ou problème de réseau' : 'Oups ! Une erreur est survenue'}
						</h2>
						<p
							style={{
								color: '#4f566b',
								fontSize: '0.95rem',
								lineHeight: 1.5,
								marginBottom: '1.75rem',
							}}
						>
							{isChunk
								? 'Une nouvelle version de l’application est disponible ou votre connexion internet a été interrompue. Veuillez rafraîchir la page pour continuer.'
								: 'Une erreur inattendue est survenue lors de l’affichage de cette page. Vous pouvez rafraîchir la page ou revenir à l’accueil.'}
						</p>
						<div
							style={{
								display: 'flex',
								gap: '0.75rem',
								justifyContent: 'center',
								flexWrap: 'wrap',
							}}
						>
							<Button color="primary" onClick={this.handleReload} data-testid="error-boundary-reload-btn">
								Rafraîchir la page
							</Button>
							<Button color="secondary" onClick={this.handleGoHome} data-testid="error-boundary-home-btn">
								Retour à l’accueil
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export default AppErrorBoundary;
